import type { AttachmentLimitsResponse, SharePointItem } from "@/api";
import { attachmentKind } from "./attachmentRules";

/** One Graph page: the most a folder listing reads (the backend's `LIST_PAGE_SIZE`). */
export const FOLDER_PAGE = 200;
/** The most files a pasted folder lists; larger folders link to SharePoint. */
export const FOLDER_SHOWN = 50;
/** A folder with this many attachable files or fewer is confirmed in one step, not picked file by file. */
export const FOLDER_CONFIRM_MAX = 10;

export interface FolderFile {
  item: SharePointItem;
  /**
   * False when chat doesn't take the file's type (the same rule as uploads). Images are left out too: a
   * SharePoint file's content is extracted as text (`/sharepoint/download`), and images have none.
   */
  usable: boolean;
}

export interface FolderContents {
  subfolders: SharePointItem[];
  /** Newest first (the backend's `limit` order), at most `FOLDER_SHOWN`. */
  files: FolderFile[];
  usable: FolderFile[];
  /** How many files the folder has, or null when it has more than one page. */
  fileCount: number | null;
}

/** Splits a folder listing (fetched with `limit`) into its subfolders and the files it offers. */
export function folderContents(
  listing: { items?: SharePointItem[]; total?: number | null },
  limits: AttachmentLimitsResponse | undefined,
): FolderContents {
  const items = listing.items ?? [];
  const subfolders = items.filter((item) => item.is_folder);
  const allFiles = items.filter((item) => !item.is_folder);
  const files = allFiles.slice(0, FOLDER_SHOWN).map((item) => ({
    item,
    usable: !limits || attachmentKind({ name: item.name, type: item.mime_type ?? "" }, limits) === "document",
  }));
  return {
    subfolders,
    files,
    usable: files.filter((file) => file.usable),
    fileCount: listing.total == null ? null : allFiles.length,
  };
}

/** Few enough files to attach them all after one confirmation (and room for every one of them). */
export function confirmsAll(contents: FolderContents, room: number): boolean {
  const count = contents.usable.length;
  return count > 0 && count <= Math.min(FOLDER_CONFIRM_MAX, room);
}
